import { RECORD_PERMISSIONS_DEFINITIONS } from '@omnea-digital/authz-model'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useAuthzRollout, type AuthzRollout } from './AuthzRolloutProvider'
import { useProvidedRecordAuthorization } from './RecordAuthorizationProvider'
import { isEntityOnPermissionsService, recordPermissionQueryOptions } from './recordAuthorizationQuery'
import { isPermitted, resolveAccess } from './resolveAccess'
import { useAccessSources } from './useAccessSources'
import type { AccessAnswer, RecordAccessRule } from './accessRules'
import type { AnyRecordAuthorization } from './useRecordAuthorization'

type PermissionQueryState = { data: boolean | undefined; isError: boolean; error: unknown }

// The same states useRecordAuthorization reports, so the rule resolves exactly as it would on a detail page.
// Only the asked verb carries an answer; the rule reads nothing else, and the result never leaves this hook.
const toRecordAuthorization = (
  rule: RecordAccessRule,
  rollout: AuthzRollout,
  { data, isError, error }: PermissionQueryState
): AnyRecordAuthorization => {
  const { entity, verb } = rule.authorization
  if (!isEntityOnPermissionsService(rollout, entity)) {
    return rollout.status === 'pending' ? { entity, status: 'loading' } : { entity, status: 'role' }
  }
  // Before isError, so a failed background refetch keeps the last-known answer; `false` is an answer too.
  if (data != null)
    return { entity, status: 'resolved', permissions: { ...RECORD_PERMISSIONS_DEFINITIONS[entity].none, [verb]: data } }
  if (isError) return { entity, status: 'unavailable', error }
  return { entity, status: 'loading' }
}

/**
 * Whether one record rule admits the caller on one row of a list, where withheld answers read as no.
 *
 * Every row asking the same rule in one tick shares a call that checks only that permission, so a page of
 * rows costs one check per row rather than one per permission the entity defines. A page that already holds
 * the record's full answer (`RecordAuthorizationProvider`) is answered from it, with no request. Detail
 * pages asking several permissions of one record keep `useIsPermitted`, which reads them all in one call.
 */
export const useIsPermittedInList = (rule: RecordAccessRule, recordId: string | undefined): boolean => {
  const { getToken, user } = useAuthenticatedUser()
  const rollout = useAuthzRollout()
  const { entity, verb } = rule.authorization
  const asked = recordId == null ? null : { entity, recordId }
  const provided = useProvidedRecordAuthorization(asked)

  const { data, isError, error } = useQuery({
    ...recordPermissionQueryOptions(getToken, user.id, provided == null && asked != null ? { ...asked, permission: verb } : null),
    enabled: isEntityOnPermissionsService(rollout, entity),
    refetchOnWindowFocus: true,
  })
  const sources = useAccessSources()

  if (recordId == null) return false
  return isPermitted(rule, { ...sources, record: provided ?? toRecordAuthorization(rule, rollout, { data, isError, error }) })
}

/** `useRecordAccessForRows` where withheld answers read as no, as in `useIsPermittedInList`. */
export const useIsPermittedForRows = (rule: RecordAccessRule, recordIds: readonly string[]): ((recordId: string) => boolean) => {
  const getRecordAccess = useRecordAccessForRows(rule, recordIds)
  return (recordId) => getRecordAccess(recordId) === 'allowed'
}

/**
 * Each row's access under one record rule, for grid callbacks that cannot call a hook per row. Withheld answers stay
 * `pending` or `unavailable`, so a row can wait on its check rather than read it as no.
 */
export const useRecordAccessForRows = (
  rule: RecordAccessRule,
  recordIds: readonly string[]
): ((recordId: string) => AccessAnswer) => {
  const { getToken, user } = useAuthenticatedUser()
  const rollout = useAuthzRollout()
  const { entity, verb } = rule.authorization
  const answers = useQueries({
    queries: recordIds.map((recordId) => ({
      ...recordPermissionQueryOptions(getToken, user.id, { entity, recordId, permission: verb }),
      enabled: isEntityOnPermissionsService(rollout, entity),
      refetchOnWindowFocus: true,
    })),
  })
  const sources = useAccessSources()
  const answerByRecordId = new Map(recordIds.map((recordId, index) => [recordId, answers[index]]))

  return (recordId) => {
    const answer = answerByRecordId.get(recordId)
    return resolveAccess(rule, {
      ...sources,
      record: toRecordAuthorization(rule, rollout, answer ?? { data: undefined, isError: false, error: null }),
    })
  }
}
